import { IFieldsMapContainerContext, IFieldsMapContainerOptions } from "../fields/map"
import { FieldsMap } from "../fields/map/fieldsMap"
import { Layer } from "../map/layer"
import { ILayer, IMapContainerUserOptions, IMapTooltip } from "../map/types"
import { arrayMax, except, groupBy } from "../utils/array"
import { isFunction } from "../utils/utils"
import { CroppingStyle } from "./croppingStyle"
import { Datalayer } from "./datalayer"
import { DrawingLayer } from "./drawing/layer"
import { PicturesMapState } from "./pictures/state"
import { getTooltip } from "./tooltip"
import { IDatalayersMap } from "./types"

const DistributionsKey = "distributions"

export class DatalayersMap extends FieldsMap implements IDatalayersMap {

    editLayer: Datalayer

    private pictures: PicturesMapState
    private croppingStyle: CroppingStyle

    contextLayers: ILayer[]

    showDistributions = true
    enableDeduplicateLabels = false


    constructor(ctxt: IFieldsMapContainerContext, options?: IFieldsMapContainerOptions) {

        super(ctxt, options)

        this.pictures = new PicturesMapState(this._map.getMap(), ctxt)
        this._disposeFunctions.push(() => this.pictures.removePictureOverlays())

        this.addTooltipProvider((tooltip: IMapTooltip) => this.getTooltip(tooltip))

        const settings = this.getModuleMapSettings()
        this.showDistributions = settings ? settings.get(DistributionsKey, true) : true

        this.manageCustomColors(true)

    }

    protected getTooltip(tooltip: IMapTooltip) {
        return getTooltip(tooltip)
    }

    getUserOptions(): IMapContainerUserOptions {
        return {
            ...super._getUserOptions(this.contextLayers),
            canDeduplicateLabels: this.enableDeduplicateLabels && this.getDataLayers().some(l => l.type && l.type.DeduplicateLabels)
        }
    }

    getCustomOptionTemplates() {
        return [
            "app/datalayers/map/options.html"
        ]
    }

    updateShowDistributions() {
        const settings = this.getModuleMapSettings()
        settings.set(DistributionsKey, this.showDistributions)
    }

    getDataLayers(filter?: (l: Datalayer) => boolean) {
        const layers = this.layers && this.layers.filter(l => l instanceof Datalayer).map(l => l as Datalayer)
        return isFunction(filter) ? layers.filter(filter) : layers
    }

    addLayer(layer: Layer, selectable = true, topmost = false) {

        super.addLayer(layer, selectable, topmost)

        if (this.editLayer) {
            this.removeLayer(this.editLayer)
            super.addLayer(this.editLayer)
        }

        this.pictures.onLayerAdded(layer, (layer == this.editLayer))
        this.refreshCustomColors()

        this.requestStackOrderUpdate()

    }

    removeLayer(layer: Layer): void {

        super.removeLayer(layer)

        this.pictures.onLayerRemoved(layer, (layer == this.editLayer))
        this.refreshCustomColors()

        this.requestStackOrderUpdate()

    }


    private _stackOrderUpdatePending: boolean

    requestStackOrderUpdate(delay = 0) {

        if (!this._stackOrderUpdatePending) {

            this._stackOrderUpdatePending = true

            if (delay > 0) {
                window.setTimeout(() => this.updateStackOrder(), delay)
            }
            else {
                window.requestAnimationFrame(() => this.updateStackOrder())
            }

        }

    }

    private updateStackOrder() {

        if (this._disposed) {
            return
        }

        this._stackOrderUpdatePending = false

        const olayers = this._map.getMap().getLayers().getArray()

        let layers = this.getLayers().filter(l => l instanceof Datalayer) as Datalayer[]
        layers = layers.filter(l => l.mapLayer && !!l.fieldId)

        const groups = groupBy(layers, l => l.fieldId)

        for (const l of groups) {

            let groupLayersSorted = l.values
                .map(l => ({ layer: l, index: olayers.indexOf(l.mapLayer) }))
                .sort((a, b) => b.index - a.index)

            const first = groupLayersSorted[0].layer

            const topmost = groupLayersSorted
                .filter(l => (l.layer === first) || ((l.layer.typeId === first.typeId) && (l.layer.croppingId !== first.croppingId)))

            for (const l of topmost) {
                l.layer.topmost = true
            }

            const topmostOpacity = arrayMax(topmost, l => l.layer.opacity)

            const others = except(groupLayersSorted, topmost)

            for (const l of others) {
                l.layer.topmost = topmostOpacity > 50 ? false : undefined
            }

        }

    }

    getTopmostLayer(predicate: (layer: Datalayer) => boolean) {

        const matches = this.getDataLayers().filter(predicate)

        if (matches.length) {
            return matches[matches.length - 1]
        }

    }

    removePictureOverlaysForLayerId(layerId: number): void {
        this.pictures.removePictureOverlaysForLayerId(layerId)
    }

    protected setupLayers() {

        super.setupLayers()

        if (this.pictures) {
            this.pictures.refresh(this.layers)
        }

        this.refreshCustomColors()

    }

    public manageCustomColors(manage: boolean) {

        if (!manage) {
            this.croppingLayer.styleProvider = null
            this.croppingStyle = null
            this.croppingLayer.refresh()
        }
        else {
            this.croppingStyle = new CroppingStyle(this.croppingLayer)
            this.croppingLayer.styleProvider = s => this.croppingStyle.getLayerStyleFunction(s)
            this.refreshCustomColors()
        }

    }

    private refreshCustomColors() {

        if (!this.croppingStyle) return

        const topmost = this.getTopmostLayer(l => true)

        const colors = (topmost instanceof DrawingLayer)
            ? topmost.getFieldColors()
            : null

        this.croppingStyle.refresh(colors)

    }

}
